/* Work opening.

   The click is a real navigation to the real Ghost post — opening a case study
   by hand and reaching it from the overview must land on the same document.
   The motion is played first, on the page we are leaving:

   1. the rows above the clicked one fly up out of view, the rows below fly
      down, staggered outward from the click
   2. the clicked row keeps its borders and accent and travels to the exact
      position the case-study header occupies on the destination
   3. once it lands the document is swapped underneath it, so the row and the
      header read as one element even though they are two

   The landing position is knowable without fetching anything: both templates
   share the shell in default.hbs, `.gh-main` carries no padding, and the
   case-study header is its first child at the top of an unscrolled scrollport.
   So the destination's header sits at this page's `.gh-main` content offset,
   and only translateY is needed to get there.

   Reduced motion and no JavaScript both fall through to the plain anchor. */
function workOpening() {
    var feed = document.querySelector('.gh-work-feed[data-work-feed]');
    if (!feed) return;

    var rows = Array.prototype.slice.call(feed.querySelectorAll('[data-work-row]'));
    if (!rows.length) return;

    var root = document.documentElement;
    var scrollport = document.querySelector('[data-scrollport]');
    var main = document.querySelector('.gh-main');
    var opening = false;

    function scrollOffset() {
        return scrollport ? scrollport.scrollTop : window.scrollY;
    }

    // The row's accent, read off the inline custom property the template writes.
    // It used to come from a duplicate data-work-accent attribute carrying a
    // second verbatim copy of the same Handlebars ladder. Two copies of a value
    // that must agree is a divergence waiting to happen — and the test guarding
    // them used String.match without /g, so it only ever compared the first.
    // One copy in the template, one read here.
    function accentOf(card) {
        return (card.style.getPropertyValue('--work-accent') || '').trim();
    }

    // Compared as paths, not as strings: the note carries whatever the case
    // study's location.href was, and the row carries whatever the template
    // wrote — one may be absolute and the other not.
    function pathOf(url) {
        try {
            return new URL(url, window.location.origin).pathname;
        } catch (e) {
            return null;
        }
    }

    function indexOfUrl(url) {
        var path = pathOf(url);
        if (!path) return -1;

        for (var i = 0; i < rows.length; i++) {
            var link = rows[i].querySelector('.gh-work-card-link');
            if (link && pathOf(link.getAttribute('href')) === path) return i;
        }
        return -1;
    }

    function motionOk() {
        return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function cssMs(name, fallback) {
        var declared = parseInt(
            window.getComputedStyle(root).getPropertyValue(name),
            10
        );
        return isNaN(declared) ? fallback : declared;
    }

    function durationMs() {
        return cssMs('--work-opening-duration-ms', 700);
    }

    function reset() {
        opening = false;
        root.classList.remove('is-work-opening');
        rows.forEach(function (row) {
            row.classList.remove('is-opening', 'is-flying', 'is-leaving');
            row.style.transform = '';
            row.style.removeProperty('--leave-y');
            row.style.removeProperty('--leave-step');
            row.style.removeProperty('--leave-scale');
        });

        var wash = document.querySelector('.gh-work-opening-wash');
        if (wash) wash.parentNode.removeChild(wash);
    }

    // The destination is a full accent page, so the accent floods down from
    // the travelling row. It is laid out where the band lands and offset back
    // to the row's current bottom edge, so it grows out of the row rather than
    // appearing above it.
    function expandBackground(card, bandBottom, rowBottom) {
        var wash = document.createElement('div');
        wash.className = 'gh-work-opening-wash';
        wash.style.top = bandBottom + 'px';
        wash.style.setProperty('--wash-from', (rowBottom - bandBottom) + 'px');

        // Stop the flood at the footer's top edge instead of the viewport
        // bottom (the CSS default), so the accent grows down to the footer and
        // leaves it standing rather than painting over it and snapping back on
        // navigation. Measured in viewport space because the wash is fixed.
        var footer = document.querySelector('.gh-footer');
        if (footer) {
            var below = window.innerHeight - footer.getBoundingClientRect().top;
            if (below > 0) wash.style.bottom = below + 'px';
        }

        var accent = accentOf(card);
        if (accent) wash.style.setProperty('--work-accent', accent);

        document.body.appendChild(wash);
        wash.getBoundingClientRect(); // start frame for the scale transition
        wash.classList.add('is-expanded');
    }

    // `step` decides the stagger, and is the only thing that differs between
    // sending the stack away and bringing it back.
    function clearRows(index, step) {
        // Two passes. Above the click leaves upward, below leaves downward, each
        // row sent just past its own viewport edge rather than a blanket 100vh.
        // That makes the rows nearest the click travel furthest, so at one shared
        // duration they were the fastest and overtook the outer rows, reshuffling
        // the stack mid-flight. Measure every distance first, then scale each
        // row's duration by it: the speed is uniform and the order is preserved.
        var plans = [];
        var maxAway = 0;
        rows.forEach(function (row, i) {
            if (i === index) return;

            var rect = row.getBoundingClientRect();

            /* Clamped at zero before the margin is added, which matters for any
               row that is already outside the viewport — on the overview that is
               routinely the last row of four, sitting below the fold.

               Unclamped, `innerHeight - rect.top` goes negative for such a row:
               it was sent *upward*, the wrong way, by a handful of pixels. That
               was invisible on the way out — the row is off-screen either way —
               but the return plays the same numbers backwards in full view, and
               |away| of six pixels also produced a --leave-scale near zero, so
               the duration collapsed with it. The row did not fly back in, it
               appeared between two frames. Both halves of the reported symptom
               are this one expression.

               Clamping makes the distance "off its own edge, plus a margin", so
               a row already past the fold still travels the margin at the shared
               speed rather than teleporting. */
            var away = i < index
                ? -(Math.max(rect.bottom, 0) + 40)
                : Math.max(window.innerHeight - rect.top, 0) + 40;

            if (Math.abs(away) > maxAway) maxAway = Math.abs(away);
            plans.push({ row: row, i: i, away: away });
        });

        plans.forEach(function (plan) {
            plan.row.style.setProperty('--leave-y', plan.away + 'px');
            plan.row.style.setProperty('--leave-step', step(Math.abs(plan.i - index)));
            /* Floored, for the same reason the distance is clamped: uniform
               speed is worth having right up until it rounds a row's duration
               down to something that reads as a cut rather than a move. */
            plan.row.style.setProperty(
                '--leave-scale',
                maxAway ? Math.max(Math.abs(plan.away) / maxAway, 0.35) : 1
            );
            plan.row.classList.add('is-leaving');
        });
    }

    // Geometry for the return trip. The case study cannot measure the overview
    // it is going back to, so the overview leaves it a note: where this row
    // sits, as a content offset, which is what it will be again on a fresh
    // unscrolled scrollport. Read and cleared by work-closing.js.
    function remember(card, index, rect, url) {
        try {
            sessionStorage.setItem('mk-work-return', JSON.stringify({
                index: index,
                top: rect.top + scrollOffset(),
                height: rect.height,
                accent: accentOf(card),
                // Which case study the note belongs to. work-closing.js keeps
                // the note now instead of consuming it, so it has to be able to
                // tell a note about this page from one about another.
                url: url
            }));
        } catch (e) {
            // Private mode or a full quota: the return just plays plain.
        }
    }

    function fly(card, url) {
        opening = true;
        root.classList.add('is-work-opening');

        var index = rows.indexOf(card);

        // .is-opening drops the hover lift with no transition attached, so the
        // measurement below is the row's rest position. Measuring first would
        // bake the 12px lift into the flight and land the box low.
        card.classList.add('is-opening');
        var rect = card.getBoundingClientRect();
        var from = rect.top;
        var to = main ? main.getBoundingClientRect().top + scrollOffset() : 0;

        remember(card, index, rect, url);
        // Outward from the click: the neighbours bolt first.
        clearRows(index, function (distance) { return distance - 1; });
        expandBackground(card, to + rect.height, rect.bottom);

        card.classList.add('is-flying');
        card.style.transform = 'translateY(' + (to - from) + 'px)';

        var navigated = false;
        var land = function () {
            if (navigated) return;
            navigated = true;
            window.location.href = url;
        };

        card.addEventListener('transitionend', function (event) {
            if (event.target === card && event.propertyName === 'transform') land();
        });
        // transitionend does not fire if the transform is interrupted or the
        // tab is backgrounded mid-flight; the reader still has to arrive.
        window.setTimeout(land, durationMs() + 150);
    }

    feed.addEventListener('click', function (event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var link = event.target.closest('.gh-work-card-link');
        if (!link || !feed.contains(link)) return;

        var card = link.closest('[data-work-row]');
        if (!card) return;

        if (opening) {
            event.preventDefault();
            return;
        }
        if (!motionOk()) return;

        event.preventDefault();
        fly(card, link.href);
    });

    // Coming back from a case study restores this document from the bfcache
    // mid-flight, with the stack flown apart. Put it back.
    window.addEventListener('pageshow', function (event) {
        if (event.persisted) reset();
    });

    /* The arrival half of the closing animation.

       The case study collapsed its accent page back onto the row's position
       and then navigated here, so the row it flew out of is already correct on
       this fresh document — nothing has to travel. What is missing is the rest
       of the stack, which flew away when the case study opened. It comes back
       the way it left, reversed: farthest first, so the neighbours of the
       returning row settle last.

       The feed is held invisible pre-paint by .is-work-returning, set in the
       head of default.hbs, because these positions can only be measured once
       the rows are laid out. */
    function playReturn() {
        var flag;
        try {
            flag = sessionStorage.getItem('mk-work-returning');
            sessionStorage.removeItem('mk-work-returning');
        } catch (e) {
            flag = null;
        }

        var done = function () {
            root.classList.remove('is-work-returning', 'is-work-arriving', 'is-work-return-band');
        };

        if (flag === null || !motionOk()) return done();

        /* The note is JSON now — work-closing.js forwards the whole saved
           geometry so the pre-paint gate can paint the band. A bare integer is
           still accepted: a tab that was mid-journey across the change, or a
           note left by an older build, should close normally rather than drop
           to the plain feed. */
        var index;
        try {
            var note = JSON.parse(flag);
            index = typeof note === 'number' ? note : note.index;

            /* A close that had no saved row sends its own URL instead, and the
               row that links there is that row — this document can look it up
               itself. Costs the band, which needs geometry only the overview
               knew, but the stack still flies back in around the right row
               instead of the whole feed appearing at once. */
            if (typeof index !== 'number' && note && note.url) index = indexOfUrl(note.url);
        } catch (e) {
            index = parseInt(flag, 10);
        }
        if (typeof index !== 'number' || isNaN(index) || index < 0 || index >= rows.length) {
            return done();
        }

        var furthest = Math.max(index, rows.length - 1 - index);
        clearRows(index, function (distance) { return furthest - distance; });

        // The returning row keeps the accent it wore as a case study for one
        // beat, so the swap from the collapsed page to this row is a fade
        // rather than a cut.
        rows[index].classList.add('is-returning');

        /* Three states, and they have to land in three separate frames.

           This used to be one `void document.body.offsetHeight` between the
           placement and the zeroing, and the rows did not fly back in at all: the
           forced layout sat BELOW the class swap, so nothing was ever flushed
           while .is-work-returning — the class carrying `transition: none` for a
           placed row — was still on, and the whole sequence collapsed into one
           style change event. The rows went from `transform: none` straight back
           to `transform: none`, there was nothing to interpolate, and the stack
           simply appeared assembled.

           Moving the reflow above the swap fixed it about half the time, which is
           worse than not fixing it. --leave-y reaches `transform` through var(),
           and transform is not a layout-affecting property, so neither forcing
           layout nor reading the resolved transform back reliably turns the
           placement into a before-change style — measured at 3 fly-ins out of 6
           on one build. Frames cannot be elided the way a forced flush can, so
           the three states are separated by real ones:

             frame 0  stack placed away, transitions suppressed, feed hidden
             frame 1  suppression dropped, feed revealed and band handed over,
                      stack still away
             frame 2  offsets zeroed — the transition now has a painted frame to
                      interpolate from, so the rows travel home

           Two frames, ~32ms, before the stack starts moving, against a fly-in
           that then runs for --work-leave-duration-ms plus its stagger.

           The band still goes in the same statement the feed is revealed in, so
           the row wearing .is-returning is already painted underneath when it
           does. Removing it any later leaves a flat band over a row that has
           started moving; any earlier reopens the gap it exists to close. */
        window.requestAnimationFrame(function () {
            root.classList.remove('is-work-returning', 'is-work-return-band');
            root.classList.add('is-work-arriving');

            window.requestAnimationFrame(function () {
                rows.forEach(function (row) {
                    row.style.setProperty('--leave-y', '0px');
                });

                /* Started here rather than alongside the placement, so the two
                   frames above are not spent out of the 60ms this budget carries
                   over the last row's stagger. */
                var stagger = cssMs('--work-leave-stagger-ms', 30);
                window.setTimeout(function () {
                    rows[index].classList.remove('is-returning');
                    reset();
                    done();
                }, cssMs('--work-leave-duration-ms', 380) + furthest * stagger + 60);
            });
        });
    }

    playReturn();
}
